import { useCallback, useMemo, useState } from 'react';

/** The queue's item shape — structurally a subset of
 *  `InvoiceDocumentOut` so the hook stays testable without the API
 *  layer. */
export interface QueueDocument {
  id: string;
  filename: string;
}

export interface InvoiceQueue<T extends QueueDocument = QueueDocument> {
  /** The document currently on the flashcard, null when the queue is
   *  exhausted (all assigned or skipped this session). */
  current: T | null;
  remaining: number;
  skippedCount: number;
  /** Move past the current card without assigning. Skipped cards
   *  re-enter at the back once every other card has been seen. */
  skip: () => void;
  /** The current card was assigned — drop it from this session's queue
   *  entirely (the server-side unassigned list no longer has it). */
  complete: (documentId: string) => void;
}

/** Session-local flashcard ordering over the unassigned pool.
 *
 *  The server owns membership (the unassigned list); this hook owns
 *  the walk: stable upload order, skip pushes to the back of the
 *  session, completion removes. Re-fetches merge naturally — a
 *  document already completed or skipped this session keeps that
 *  state; genuinely new documents append in server order.
 */
export function useInvoiceQueue<T extends QueueDocument>(documents: T[]): InvoiceQueue<T> {
  const [completed, setCompleted] = useState<ReadonlySet<string>>(new Set());
  const [skipped, setSkipped] = useState<readonly string[]>([]);

  const pending = useMemo(
    () => documents.filter((d) => !completed.has(d.id) && !skipped.includes(d.id)),
    [documents, completed, skipped],
  );
  const skippedDocs = useMemo(
    () =>
      skipped
        .map((id) => documents.find((d) => d.id === id))
        .filter((d): d is T => d !== undefined && !completed.has(d.id)),
    [documents, skipped, completed],
  );

  // Fresh cards first (upload order), then skipped cards in the order
  // they were skipped.
  const ordered = useMemo(() => [...pending, ...skippedDocs], [pending, skippedDocs]);
  const current = ordered[0] ?? null;

  const skip = useCallback(() => {
    if (current === null) return;
    const id = current.id;
    setSkipped((prev) => (prev.includes(id) ? [...prev.filter((s) => s !== id), id] : [...prev, id]));
  }, [current]);

  const complete = useCallback((documentId: string) => {
    setCompleted((prev) => new Set(prev).add(documentId));
    setSkipped((prev) => prev.filter((s) => s !== documentId));
  }, []);

  return {
    current,
    remaining: ordered.length,
    skippedCount: skippedDocs.length,
    skip,
    complete,
  };
}
